import React from 'react';
import {
  AlarmClock, AlertTriangle, Archive, ArrowDown, ArrowRight, ArrowUp, Ban, BarChart3, Bell, BookMarked,
  Bookmark, Bot, Brain, Briefcase, Building2, Calendar, CalendarDays, Camera, CheckCircle2, Clapperboard,
  Clock, Download, DollarSign, Dumbbell, Eye, EyeOff, Factory, FileText, Flame, Folder, FolderOpen,
  Frown, Gamepad2, Gem, Globe, GraduationCap, Hand, Handshake, Hash, Headphones, HelpCircle, Hourglass,
  IdCard, Image as ImageIcon, Key, Landmark, Library, LifeBuoy, Lightbulb, Link2, Lock, Mail, MailOpen,
  Mailbox, MapPin, Medal, Megaphone, Meh, MessageCircle, MessageSquare, Mic, Monitor, Moon, MousePointer2,
  Music, Music2, Package, Paintbrush, Palette, Paperclip, PartyPopper, Pause, PenLine, PenTool, Pencil,
  Phone, Pin, Plug, Plus, Projector, Puzzle, Radio, Receipt, Recycle, RefreshCw, Repeat, Rocket, Save,
  Scale, ScrollText, Search, Send, Settings, Shuffle, Siren, SlidersHorizontal, Smartphone, Smile,
  Snowflake, Sparkle, Sparkles, Square, Star, Swords, Tag, Target, ThumbsUp, Timer, TrendingDown,
  TrendingUp, Trash2, Trophy, Tv, Type, Upload, User, Users, Wand2, Wrench, XCircle, Zap, Inbox,
  GalleryHorizontal,
} from 'lucide-react';

// Emoji -> ícone de linha (lucide). Mantém o visual do NexoProject: traço fino, cor herdada do texto.
const ICONS = {
  '✅': CheckCircle2, '🎯': Target, '📧': Mail, '📋': FileText, '📊': BarChart3, '⚠': AlertTriangle,
  '🚀': Rocket, '⏳': Hourglass, '🔍': Search, '🔄': RefreshCw, '📝': FileText, '🗑': Trash2,
  '🎨': Palette, '✏': Pencil, '📅': Calendar, '🤖': Bot, '🔗': Link2, '❌': XCircle, '💼': Briefcase,
  '💡': Lightbulb, '👤': User, '⚡': Zap, '👥': Users, '📄': FileText, '👁': Eye, '💬': MessageSquare,
  '✨': Sparkles, '📞': Phone, '💾': Save, '🤝': Handshake, '🖼': ImageIcon, '📱': Smartphone,
  '🌐': Globe, '🏢': Building2, '💰': DollarSign, '📈': TrendingUp, '⚙': Settings, '📍': MapPin,
  '📁': Folder, '🧠': Brain, '🎵': Music, '➕': Plus, '⏸': Pause, '⏱': Timer, '📤': Upload,
  '📦': Package, '🎙': Mic, '🏆': Trophy, '✉': Mail, '🎉': PartyPopper, '📥': Download, '🔔': Bell,
  '📎': Paperclip, '🙈': EyeOff, '📌': Pin, '🎛': SlidersHorizontal, '🏷': Tag, '✍': PenLine,
  '🎬': Clapperboard, '🧑': User, '⏰': AlarmClock, '➡': ArrowRight, '🚨': Siren, '📂': FolderOpen,
  '⬇': ArrowDown, '📭': Inbox, '🔮': Wand2, '🔀': Shuffle, '🖥': Monitor, '🔥': Flame, '⬆': ArrowUp,
  '🔧': Wrench, '🔢': Hash, '😊': Smile, '👋': Hand, '📬': Mailbox, '📨': Send, '⚖': Scale,
  '🕐': Clock, '🔲': Square, '🏭': Factory, '💪': Dumbbell, '📷': Camera, '🆕': Sparkle, '🆔': IdCard,
  '📜': ScrollText, '🖱': MousePointer2, '👀': Eye, '🥇': Medal, '🥈': Medal, '🥉': Medal,
  '🎮': Gamepad2, '📢': Megaphone, '🔑': Key, '🎼': Music2, '🔌': Plug, '⬜': Square, '😟': Frown,
  '😐': Meh, '🔁': Repeat, '👍': ThumbsUp, '❄': Snowflake, '🎠': GalleryHorizontal, '📽': Projector,
  '⭐': Star, '📚': Library, '⚔': Swords, '🎤': Mic, '🌍': Globe, '🔎': Search, '📆': Calendar,
  '🗓': CalendarDays, '👔': Briefcase, '🖌': Paintbrush, '🆘': LifeBuoy, '🏛': Landmark, '♻': Recycle,
  '🌀': RefreshCw, '🗄': Archive, '🔤': Type, '🖊': PenTool, '📺': Tv, '📉': TrendingDown,
  '📲': Smartphone, '🌎': Globe, '🔐': Lock, '💥': Zap, '🗨': MessageCircle, '📸': Camera,
  '📡': Radio, '🔖': Bookmark, '🧩': Puzzle, '🎧': Headphones, '📣': Megaphone, '🚫': Ban,
  '⏹': Square, '💎': Gem, '💤': Moon, '❓': HelpCircle, '📑': BookMarked, '🧾': Receipt, '💌': Mail,
  '🎓': GraduationCap, '📩': Mail,
};

// Cores fixas para ícones que carregam significado (medalhas)
const ICON_TONE = { '🥇': 'text-amber-500', '🥈': 'text-slate-400', '🥉': 'text-orange-700' };

// Bolinhas coloridas de status
const DOTS = {
  '🔴': 'bg-red-500', '🔵': 'bg-blue-500', '🟡': 'bg-yellow-400', '🟢': 'bg-green-500',
  '🟠': 'bg-orange-500', '🟣': 'bg-purple-500', '⚫': 'bg-slate-800', '⚪': 'bg-white border border-slate-300',
  '🔶': 'bg-orange-400 rotate-45 rounded-[2px]',
};

export const normalizeEmoji = (emoji) => String(emoji || '').replace(/[️‍]/g, '');

export default function Ico({ e, className = '', bare = false }) {
  const key = normalizeEmoji(e);
  const spacing = bare ? '' : 'mr-1.5';
  if (DOTS[key]) {
    const shape = key === '🔶' ? '' : 'rounded-full';
    return <span aria-hidden="true" className={`inline-block h-2.5 w-2.5 shrink-0 align-middle ${spacing} ${shape} ${DOTS[key]} ${className}`} />;
  }
  const Icon = ICONS[key];
  if (!Icon) return null;
  return (
    <Icon
      aria-hidden="true"
      strokeWidth={1.75}
      className={`inline-block h-[1.1em] w-[1.1em] shrink-0 align-[-0.18em] ${spacing} ${ICON_TONE[key] || ''} ${className}`}
    />
  );
}

export const hasIco = (emoji) => Boolean(ICONS[normalizeEmoji(emoji)] || DOTS[normalizeEmoji(emoji)]);
